import { AsyncTaskProgressDrawer } from '@dealstack/frontend/shared/components/AsyncTaskProgressDrawer/AsyncTaskProgressDrawer';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex, Text } from '@mantine/core';
import { memo } from 'react';

import { ErrorsTable } from './ErrorsTable';
import { RowError } from './ErrorsTable.types';

interface Props {
	opened: boolean;
	onClose: () => void;
	createdEntitiesNumber?: number;
	status?: string;
	progressPercentage?: number;
	totalProcessed?: number;
	total?: number;
	rowErrors?: RowError[];
	systemError?: string;
}

const BulkUploadResultDrawer = ({
	opened,
	onClose,
	createdEntitiesNumber,
	status,
	progressPercentage,
	totalProcessed,
	total,
	rowErrors = [],
	systemError,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const inProgressBody = (
		<>
			<Text>
				{tCapManage('transactions.bulk_upload_drawer.progress_text', {
					totalProcessed,
					total,
				})}
			</Text>
			{rowErrors.length > 0 && (
				<Flex direction="column" mt={16}>
					<Text fw={600} c="red" size="sm">
						{tCapManage('transactions.bulk_upload_drawer.errors_found', {
							count: rowErrors.length,
						})}
					</Text>
					<ErrorsTable errors={rowErrors} maxHeight={400} />
				</Flex>
			)}
		</>
	);

	const renderErrorBody = () => {
		if (rowErrors.length > 0) {
			return (
				<>
					<Text fw={600} size="sm" mb={8}>
						{tCapManage('transactions.bulk_upload_drawer.errors_rolled_back', {
							count: rowErrors.length,
						})}
					</Text>
					<ErrorsTable errors={rowErrors} maxHeight={300} />
				</>
			);
		}
		if (systemError) {
			return <Text size="sm">{systemError}</Text>;
		}
		return null;
	};

	return (
		<AsyncTaskProgressDrawer
			opened={opened}
			onClose={onClose}
			status={status}
			progressPercentage={progressPercentage}
			title={tCapManage('transactions.bulk_upload_drawer.entities_added_title')}
			inProgressAlert={{
				title: tCapManage('transactions.bulk_upload_drawer.processing_transactions_title'),
				description: tCapManage(
					'transactions.bulk_upload_drawer.processing_transactions_detail'
				),
			}}
			doneAlert={{
				title: tCapManage('transactions.bulk_upload_drawer.processed_transactions_title', {
					count: createdEntitiesNumber,
				}),
				description: tCapManage(
					'transactions.bulk_upload_drawer.processed_transactions_detail',
					{ count: createdEntitiesNumber }
				),
			}}
			errorAlert={{
				title: tCapManage(
					'transactions.bulk_upload_drawer.processing_transactions_error_title'
				),
				description: tCapManage(
					'transactions.bulk_upload_drawer.processing_transactions_error_detail'
				),
			}}
			inProgressBody={inProgressBody}
			errorBody={renderErrorBody()}
			closeLabel={tCapManage('transactions.bulk_upload_drawer.done_button')}
		/>
	);
};

const _BulkUploadResultDrawerExport = memo(BulkUploadResultDrawer);

export { _BulkUploadResultDrawerExport as BulkUploadResultDrawer };
